import React, { useState } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Row,
  Col,
  Divider,
  message,
  Popconfirm,
} from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  TagOutlined,
  DownloadOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  PaperClipOutlined,
  SyncOutlined,
  EditOutlined,
  DeleteOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import CustomDrawer from "../../../../common/custom-drawer/custom-drawer";
import styles from "./details-card.module.css";
import { deleteAnnouncement } from "../../../../services/announcement";
import { toast } from "react-toastify";
import TextFilePreview from "../text-viewer/test-viewer";
import DocxFilePreview from "../docx-viewer/docx-viewer";

const { Text, Title, Paragraph } = Typography;

const NotificationCard = ({
  title,
  description,
  updated_at,
  announcement_for,
  level,
  attachments = [],
  onViewDetails,
  announcementId,
  handleDelete,
  cardIcon,
  cardColor = "#1890ff",
  onEdit,
  announcement,
}) => {
  const processLevel = (level) => {
    if (!level) return "";

    if (Array.isArray(level)) {
      const items = level
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }
          if (typeof item === "object" && item.name) {
            return item.name;
          }
          return String(item);
        })
        .filter((item) => item.length > 0);
      return items.join(", ");
    }

    if (typeof level === "string") {
      return level;
    }

    if (typeof level === "object" && level.name) {
      return level.name;
    }

    return String(level);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getFileIcon = (mimeType, fileName) => {
    if (!mimeType) {
      if (fileName) {
        const ext = fileName.split(".").pop()?.toLowerCase();
        if (ext === "pdf")
          return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
        if (["doc", "docx"].includes(ext))
          return <FileWordOutlined style={{ color: "#1890ff" }} />;
        if (["xls", "xlsx"].includes(ext))
          return <FileExcelOutlined style={{ color: "#52c41a" }} />;
      }
      return <FileOutlined style={{ color: "#8c8c8c" }} />;
    }

    const lowerMimeType = mimeType.toLowerCase();

    if (lowerMimeType.includes("pdf")) {
      return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    } else if (lowerMimeType.includes("image")) {
      return <FileImageOutlined style={{ color: "#fa8c16" }} />;
    } else if (
      lowerMimeType.includes("word") ||
      lowerMimeType.includes("document")
    ) {
      return <FileWordOutlined style={{ color: "#1890ff" }} />;
    } else if (
      lowerMimeType.includes("excel") ||
      lowerMimeType.includes("spreadsheet")
    ) {
      return <FileExcelOutlined style={{ color: "#52c41a" }} />;
    } else if (lowerMimeType.includes("video")) {
      return <VideoCameraOutlined style={{ color: "#722ed1" }} />;
    } else if (lowerMimeType.includes("audio")) {
      return <AudioOutlined style={{ color: "#eb2f96" }} />;
    } else {
      return <FileOutlined style={{ color: "#8c8c8c" }} />;
    }
  };

  const processedLevel = processLevel(level);

  return (
    <Card
      style={{
        width: "100%",
        height: "100%",
        borderRadius: 12,
        border: "1px solid #e8e8e8",
        marginBottom: 16,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      bodyStyle={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: 1,
      }}
    >
      <div className={styles.titleContainerWrapper}>
        <Space align="start" size={12} style={{ width: "100%" }}>
          <div style={{ display: "flex", minWidth: 0 }} className={styles.titleContainer}>
            <Title className={styles.titleValue} level={5}>
              <NotificationOutlined /> {title}
            </Title>
            <div className={styles.actionIcons}>
              <EditOutlined onClick={() => onEdit(announcement)} />
              <Popconfirm
                title={`Are you sure you want to delete ?`}
                onConfirm={handleDelete}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined />
              </Popconfirm>
            </div>
          </div>
        </Space>
      </div>

      <div
        style={{
          padding: "12px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ flex: 1 }}>
          {/* Description */}
          {description && (
            <p className={styles["card-description"]}>{description}</p>
          )}

          <div style={{ marginBottom: 16 }}>
            {updated_at && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <CalendarOutlined
                  style={{ marginRight: 8, fontSize: 12, color: "#9ca3af" }}
                />
                <Text style={{ fontSize: 12, color: "#6b7280" }}>
                  Date Posted: {formatDate(updated_at)}
                </Text>
              </div>
            )}

            {announcement_for && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <UserOutlined
                  style={{ marginRight: 8, fontSize: 12, color: "#9ca3af" }}
                />
                <Text style={{ fontSize: 12, color: "#6b7280" }}>
                  Announcement For: {announcement_for}
                </Text>
              </div>
            )}

            {level && processedLevel && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: 8,
                }}
              >
                <TagOutlined
                  style={{
                    marginRight: 8,
                    fontSize: 12,
                    marginTop: 2,
                    flexShrink: 0,
                    color: "#9ca3af",
                  }}
                />
                <Text
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginRight: 6,
                    flexShrink: 0,
                  }}
                >
                  Level:
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    wordBreak: "break-word",
                    lineHeight: "16px",
                    flex: 1,
                  }}
                >
                  {processedLevel}
                </Text>
              </div>
            )}
          </div>
        </div>

        {attachments?.length > 0 && (
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 4,
              padding: "0px 10px",
              marginBottom: 16,
            }}
          >
            {attachments.map((attachment, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 0",
                }}
              >
                <div style={{ fontSize: 16, flexShrink: 0 }}>
                  {getFileIcon(attachment.mimeType, attachment.name)}
                </div>
                <Text
                  style={{
                    fontSize: 13,
                    color: "#374151",
                    cursor: "pointer",
                    flex: 1,
                    fontWeight: 500,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() =>
                    attachment.onDownload && attachment.onDownload()
                  }
                >
                  {attachment.name}
                </Text>
                {attachment.onDownload && (
                  <Button
                    type="text"
                    size="small"
                    icon={
                      <DownloadOutlined
                        style={{ fontSize: 14, color: "#6b7280" }}
                      />
                    }
                    onClick={attachment.onDownload}
                    style={{
                      padding: "4px",
                      width: 28,
                      height: 28,
                      minWidth: 28,
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "transparent",
                      border: "1px solid #e2e8f0",
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div>
          <Button
            type="primary"
            block
            onClick={onViewDetails}
            style={{
              backgroundColor: "#10b981",
              borderColor: "#10b981",
              borderRadius: 8,
              height: 40,
              fontSize: 14,
              fontWeight: 600,
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            View Details
          </Button>
        </div>
      </div>
    </Card>
  );
};

const NotificationCardExample = ({ announcements, setReload, onEdit }) => {
  const [detailsDrawer, setDetailsDrawer] = useState(null);

  const handleFileDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/octet-stream",
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName || "download";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Download failed:", error);
      message.error("Download failed. Please try again.");
    }
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "download";

    try {
      const urlParts = url.split("/");
      const lastPart = urlParts[urlParts.length - 1];
      const decodedPart = decodeURIComponent(lastPart);
      if (decodedPart.includes("#")) {
        return decodedPart.split("#")[1];
      }
      return decodedPart.split("?")[0];
    } catch (error) {
      return "download";
    }
  };

  const handleDelete = async (data) => {
    try {
      const res = await deleteAnnouncement(data);
      if (res?.data?.status === true) {
        toast.success("Announcement Deleted !");
        setReload(true);
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  const transformedAnnouncements =
    announcements?.map((announcement, index) => {
      const attachments = [];
      if (announcement.file_url) {
        const fileName = getFileNameFromUrl(announcement.file_url);
        attachments.push({
          name: fileName,
          mimeType: announcement.file_mimetype,
          onDownload: () => handleFileDownload(announcement.file_url, fileName),
        });
      }

      return {
        id: announcement.id,
        title: announcement.title,
        description: announcement.description,
        updated_at: announcement.updated_at,
        announcement_for: announcement.announcement_for,
        levels: announcement.levels,
        attachments: attachments,
        onViewDetails: () => setDetailsDrawer(announcement),
        onEdit: () => onEdit(announcement),
        announcement: announcement,
      };
    }) || [];

  return (
    <div
      style={{
        maxWidth: 1200,
        margin: "0 auto",
        minHeight: "100vh",
      }}
    >
      <Row gutter={[8, 8]}>
        {transformedAnnouncements.map((item, index) => (
          <Col
            xs={24}
            sm={12}
            lg={8}
            xl={6}
            key={index}
            style={{ display: "flex" }}
          >
            <NotificationCard
              announcementId={item.id}
              title={item.title}
              description={item.description}
              updated_at={item.updated_at}
              announcement_for={item.announcement_for}
              level={item.levels}
              attachments={item.attachments}
              onViewDetails={item.onViewDetails}
              cardIcon={item.cardIcon}
              cardColor={item.cardColor}
              handleDelete={() => handleDelete(item.id)}
              onEdit={onEdit}
              announcement={item.announcement}
            />
          </Col>
        ))}
      </Row>

      {/* Details Drawer */}
      {detailsDrawer && (
        <CustomDrawer
          title="Announcement Details"
          open={detailsDrawer !== null}
          onClose={() => {
            setDetailsDrawer(null);
          }}
          width="45vw"
          className={styles.drawer}
          drawerStyles={{
            body: { padding: 0, margin: 0, backgroundColor: "#fafafa" },
          }}
        >
          <div
            id="desc_container"
            style={{
              padding: 20,
            }}
          >
            <>
              <div style={{ marginBottom: 16 }}>
                <Space align="center">
                  <Title level={4} style={{ margin: 0 }}>
                    {detailsDrawer?.title}
                  </Title>
                </Space>
              </div>

              <Divider />

              <div style={{ marginBottom: 24 }}>
                <Title
                  level={5}
                  style={{
                    marginBottom: 8,
                    color: "#666",
                    textTransform: "uppercase",
                    fontSize: 12,
                    letterSpacing: "0.5px",
                  }}
                >
                  Description:
                </Title>
                <Paragraph
                  style={{ marginBottom: 0, fontSize: 14, lineHeight: 1.6 }}
                >
                  {detailsDrawer?.description}
                </Paragraph>
              </div>

              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 120,
                      color: "#666",
                    }}
                  >
                    <CalendarOutlined style={{ marginRight: 8 }} />
                    <Text strong>Date Posted:</Text>
                  </div>
                  <Text>
                    {new Date(detailsDrawer?.created_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      }
                    )}
                  </Text>
                </div>

                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 120,
                      color: "#666",
                    }}
                  >
                    <UserOutlined style={{ marginRight: 8 }} />
                    <Text strong>Audience:</Text>
                  </div>
                    {detailsDrawer?.announcement_for}
                </div>

                {/* Updated */}
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 120,
                      color: "#666",
                    }}
                  >
                    <SyncOutlined style={{ marginRight: 8 }} />
                    <Text strong>Updated:</Text>
                  </div>
                  <Text>
                    {new Date(detailsDrawer?.updated_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      }
                    )}
                  </Text>
                </div>

                {detailsDrawer?.file_url && (
                  <div style={{ marginBottom: 24 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 12,
                      }}
                    >
                      <PaperClipOutlined
                        style={{ marginRight: 8, color: "#666" }}
                      />
                      <Text strong>Attachment:</Text>
                      <Text
                        style={{ marginLeft: 8, fontSize: 12, color: "#666" }}
                      >
                        {getFileNameFromUrl(detailsDrawer?.file_url)}
                      </Text>
                    </div>

                    <div
                      style={{
                        backgroundColor: "#fff",
                        border: "1px solid #e8e8e8",
                        borderRadius: 8,
                        overflow: "hidden",
                      }}
                    >
                      {detailsDrawer?.file_url && (
                        <div style={{ marginBottom: 24 }}>
                          <div
                            style={{
                              backgroundColor: "#fff",
                              border: "1px solid #e8e8e8",
                              borderRadius: 8,
                              overflow: "hidden",
                            }}
                          >
                            {detailsDrawer?.file_mimetype?.includes(
                              "image/"
                            ) ? (
                              <div
                                style={{ textAlign: "center", padding: "20px" }}
                              >
                                <img
                                  src={detailsDrawer.file_url}
                                  alt={getFileNameFromUrl(
                                    detailsDrawer.file_url
                                  )}
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "400px",
                                    objectFit: "contain",
                                    borderRadius: "4px",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display =
                                      "block";
                                  }}
                                />
                                <div
                                  style={{
                                    display: "none",
                                    color: "#666",
                                    padding: "20px",
                                  }}
                                >
                                  <FileOutlined
                                    style={{
                                      fontSize: "32px",
                                      marginBottom: "8px",
                                    }}
                                  />
                                  <p>Unable to load image</p>
                                </div>
                              </div>
                            ) : detailsDrawer.file_mimetype?.includes(
                                "application/pdf"
                              ) ||
                              getFileNameFromUrl(detailsDrawer.file_url)
                                .toLowerCase()
                                .endsWith(".pdf") ? (
                              // PDF preview
                              <div style={{ height: "500px", width: "100%" }}>
                                <iframe
                                  src={detailsDrawer.file_url}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                    transform: "scale(0.9)",
                                  }}
                                  title={getFileNameFromUrl(
                                    detailsDrawer.file_url
                                  )}
                                  onError={() => {
                                    console.log("PDF load error");
                                  }}
                                />
                              </div>
                            ) : detailsDrawer.file_mimetype?.includes(
                                "text/plain"
                              ) ||
                              getFileNameFromUrl(detailsDrawer.file_url)
                                .toLowerCase()
                                .endsWith(".txt") ? (
                              <TextFilePreview
                                fileUrl={detailsDrawer.file_url}
                              />
                            ) : detailsDrawer.file_mimetype?.includes(
                                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              ) ||
                              getFileNameFromUrl(detailsDrawer.file_url)
                                .toLowerCase()
                                .endsWith(".docx") ? (
                              <DocxFilePreview
                                fileUrl={detailsDrawer.file_url}
                              />
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "40px",
                                  color: "#666",
                                }}
                              >
                                <FileOutlined
                                  style={{
                                    fontSize: "48px",
                                    marginBottom: "16px",
                                    color: "#ccc",
                                  }}
                                />
                                <p style={{ margin: 0, marginBottom: "8px" }}>
                                  Preview not available for this file type
                                </p>
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "12px" }}
                                >
                                  {detailsDrawer.file_mimetype ||
                                    "Unknown file type"}
                                </Text>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Space>

              <Divider />
            </>

            <div
              style={{
                padding: "16px 14px",
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
                position: "fixed",
                bottom: 0,
                width: "100%",
              }}
            >
              <Button onClick={() => setDetailsDrawer(null)}>Close</Button>
            </div>
          </div>
        </CustomDrawer>
      )}
    </div>
  );
};

export default NotificationCardExample;
